import React from "react"
import Typography from "aldoo-ra/Typography"
import { CheckIcon, ArrowPathIcon } from "@heroicons/react/24/outline"

/**
 * InstallProgressSteps - Displays step-by-step installation progress
 *
 * @param {Object} props
 * @param {Object} props.steps - Object containing step states { creating, sending, installing }
 * @param {string} props.currentStep - Current active step ('creating', 'sending', 'installing', 'completed')
 * @param {string} props.currentTaskName - Current task name/label
 * @param {string} props.status - Overall status ('running', 'completed', 'error')
 * @param {string} props.error - Error message if status is 'error'
 */
export default function InstallProgressSteps({ steps, currentStep, currentTaskName, status, error }) {
  if (!steps) return null

  const stepOrder = [
    { key: 'creating', label: 'Build' },
    { key: 'sending', label: 'Send' },
    { key: 'installing', label: 'Install' }
  ]

  const getStepState = (stepKey) => {
    return steps[stepKey]?.status || 'pending'
  }

  const renderStepCircle = (stepKey, stepState) => {
    const isCompleted = stepState === 'completed'
    const isActive = stepState === 'active'

    return (
      <div className={`
        w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all
        ${isCompleted
          ? 'bg-admin_success border-admin_success'
          : isActive
          ? 'bg-admin_canvas border-admin_primary'
          : 'bg-transparent border-admin_bordered'
        }
      `}>
        {isCompleted ? (
          <CheckIcon className="w-4 h-4 text-white" />
        ) : isActive ? (
          <ArrowPathIcon className="w-4 h-4 text-admin_primary animate-spin" />
        ) : null}
      </div>
    )
  }

  const renderConnectingLine = (prevStepKey, currentStepKey) => {
    const prevState = getStepState(prevStepKey)
    const isCompleted = prevState === 'completed'

    return (
      <div className="flex-1 h-0.5 mx-2">
        <div className={`h-full transition-all ${
          isCompleted ? 'bg-admin_success' : 'bg-admin_bordered'
        }`} />
      </div>
    )
  }

  return (
    <div className="bg-admin_canvas rounded-lg p-6 border border-admin_bordered">
      {/* Horizontal step indicator */}
      <div className="mb-8">
        <div className="flex items-start">
          {stepOrder.map((step, index) => {
            const stepState = getStepState(step.key)

            return (
              <React.Fragment key={step.key}>
                {/* Step container with circle and label */}
                <div className="flex flex-col items-center gap-3">
                  {/* Step circle */}
                  {renderStepCircle(step.key, stepState)}

                  {/* Label below circle */}
                  <Typography
                    variant="admin_txt2"
                    className={`
                      whitespace-nowrap transition-all text-center
                      ${stepState === 'completed'
                        ? 'text-admin_success font-semibold'
                        : stepState === 'active'
                        ? 'text-admin_primary font-semibold'
                        : 'text-admin_text_secondary'
                      }
                    `}
                  >
                    {step.label}
                  </Typography>
                </div>

                {/* Connecting line (except after last step) */}
                {index < stepOrder.length - 1 && (
                  <div className="flex items-center flex-1" style={{ height: '32px' }}>
                    {renderConnectingLine(step.key, stepOrder[index + 1].key)}
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {/* Current task label */}
      {currentTaskName && currentStep !== 'completed' && status !== 'error' && (
        <div className="text-center">
          <Typography variant="admin_txt1_dim">
            {currentTaskName}
          </Typography>
        </div>
      )}

      {/* Completed message */}
      {currentStep === 'completed' && (
        <div className="text-center">
          <Typography 
            variant="admin_txt2" 
            className="text-admin_success font-semibold"
          >
            ✓ Installation Completed Successfully
          </Typography>
        </div>
      )}

      {/* Error message */}
      {status === 'error' && (
        <div className="text-center">
          <Typography 
            variant="admin_txt2" 
            className="text-admin_error font-semibold"
          >
            ✗ Installation Failed: {error || 'Unknown error'}
          </Typography>
        </div>
      )}
    </div>
  )
}
